import Cookies from 'js-cookie';
import { csrfFetch } from './csrf.js';
import { gcoreServerUrl } from '../config';

/** ADMIN이 고른 거래선. 비어 있으면 로그인 회사. */
let actingGclientId = '';

export const setActingGclientId = (id) => {
  actingGclientId = id ? String(id) : '';
};

export const actingGclientHeaders = (headers = {}) => {
  if (actingGclientId) headers['X-SubMat-Gclient-Id'] = actingGclientId;
  return headers;
};

const subMatFetch = (dispatch, url, options = {}) => {
  const headers = actingGclientHeaders({ ...(options.headers || {}) });
  return csrfFetch(dispatch, url, { ...options, headers });
};

const SET_SUB_MAT_FOLDERS = 'subMatFolder/setSubMatFolders';
const SET_SUB_MAT_FOLDER = 'subMatFolder/setSubMatFolder';
const SET_SUB_MAT_FOLDER_FILES = 'subMatFolder/setSubMatFolderFiles';
const ADD_SUB_MAT_FOLDER = 'subMatFolder/addSubMatFolder';
const SET_SUB_MAT_FOLDER_LINKS = 'subMatFolder/setSubMatFolderLinks';

const setSubMatFolders = (payload) => ({
  type: SET_SUB_MAT_FOLDERS,
  payload,
});

const setSubMatFolder = (subMatFolder) => ({
  type: SET_SUB_MAT_FOLDER,
  payload: subMatFolder,
});

const setSubMatFolderFiles = (payload) => ({
  type: SET_SUB_MAT_FOLDER_FILES,
  payload,
});

/** 현재 폴더는 유지하고 목록에만 넣는다 */
const addSubMatFolder = (subMatFolder) => ({
  type: ADD_SUB_MAT_FOLDER,
  payload: subMatFolder,
});

const setSubMatFolderLinks = (subMatFolderLinks) => ({
  type: SET_SUB_MAT_FOLDER_LINKS,
  payload: subMatFolderLinks,
});

export const ROOT_KIND_LABEL = {
  GENERAL: '일반문서',
  CERTIFICATION: '인증서',
  TEST: '성적서',
  ETC: '기타',
};

/** UI 용어: current → 「사용중」 */
export const CURRENT_FILE_LABEL = '사용중';

/** 우클릭 메뉴 — 다른 버전을 사용중으로 전환 */
export const SET_CURRENT_VERSION_LABEL = '이 버전 사용';

/** 속성이 있으면 그 폴더가 문서를 대표한다. */
export const folderHasAttribute = (folder) => {
  const spec = folder?.spec;
  if (!spec || typeof spec !== 'object') return false;
  if (folder.rootKind === 'GENERAL') return Boolean(spec.g04GeneralDocuId);
  return Boolean(spec.g04docuGCertificationId);
};

/** 사용중 문서 있으면 🗂️, 없으면 📁. 문서명·제품명·규격명은 아이콘 우측 하단 사각형으로 구분한다. */
export const folderEmoji = (folder) => (
  folder?.currentFile ? '🗂️' : '📁'
);

/** 속성 폴더와, 그 폴더까지 가는 상위 폴더. 루트는 항상 남긴다. */
export const foldersForSpecView = (folders) => {
  const byId = new Map((folders || []).map((folder) => [folder.id, folder]));
  const keep = new Set();
  (folders || []).forEach((folder) => {
    if (!isSystemRootFolder(folder) && !folderHasAttribute(folder)) return;
    let current = folder;
    while (current) {
      keep.add(current.id);
      current = current.parentId ? byId.get(current.parentId) : null;
    }
  });
  return (folders || []).filter((folder) => keep.has(folder.id));
};

/** HTML5 DnD — 트리 ↔ 중간 탐색기 폴더 이동/복사 */
export const DND_FOLDER_TYPE = 'application/x-submat-folder-id';

/** 포인터/HTML5 공통 — 드롭 대상 폴더 id 속성 */
export const DROP_FOLDER_ATTR = 'data-submat-drop-folder-id';

export const isSystemRootFolder = (folder) => Boolean(
  folder && folder.isSystem && folder.folderLevel === 0,
);

/** 드래그 중 소스 id 목록 (HTML5 types가 비는 브라우저 대비 + 포인터 DnD) */
let activeFolderDragIds = [];

export const beginFolderDrag = (folderIdOrIds) => {
  if (Array.isArray(folderIdOrIds)) {
    activeFolderDragIds = folderIdOrIds.filter(Boolean);
  } else if (folderIdOrIds) {
    activeFolderDragIds = [folderIdOrIds];
  } else {
    activeFolderDragIds = [];
  }
};

export const endFolderDrag = () => {
  activeFolderDragIds = [];
};

export const getActiveFolderDragId = () => activeFolderDragIds[0] || null;

export const getActiveFolderDragIds = () => activeFolderDragIds.slice();

export const isFolderDrag = (dataTransfer) => (
  activeFolderDragIds.length > 0
  || Array.from(dataTransfer?.types || []).includes(DND_FOLDER_TYPE)
);

export const setFolderDragData = (dataTransfer, folderIdOrIds) => {
  const ids = (Array.isArray(folderIdOrIds) ? folderIdOrIds : [folderIdOrIds]).filter(Boolean);
  beginFolderDrag(ids);
  dataTransfer.setData(DND_FOLDER_TYPE, JSON.stringify(ids));
  dataTransfer.setData('text/plain', ids.join(','));
  dataTransfer.effectAllowed = 'copyMove';
};

export const getFolderDragIds = (dataTransfer) => {
  const raw = dataTransfer?.getData(DND_FOLDER_TYPE) || '';
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (_e) {
      return [raw];
    }
  }
  return getActiveFolderDragIds();
};

export const getFolderDragId = (dataTransfer) => getFolderDragIds(dataTransfer)[0] || '';

export const findDropFolderIdAtPoint = (clientX, clientY) => {
  const el = document.elementFromPoint(clientX, clientY);
  const dropEl = el?.closest?.(`[${DROP_FOLDER_ATTR}]`);
  return dropEl?.getAttribute?.(DROP_FOLDER_ATTR) || null;
};

/** 시스템 4루트와 추가 서류로 보이는 줄은 업로드하지 않는다. */
export const isUploadableFolder = (folder) => (
  folder && !folder.linkView && !isSystemRootFolder(folder)
);

const foldersById = (folders) => {
  if (!folders) return new Map();
  if (folders instanceof Map) return folders;
  if (Array.isArray(folders)) return new Map(folders.map((folder) => [folder.id, folder]));
  return new Map(Object.values(folders).filter(Boolean).map((folder) => [folder.id, folder]));
};

/** 성적서에서 인증규격과 제품명을 가진 폴더. 두께는 아래 규격 폴더에 둔다. */
export const isNamedProductFolder = (folder) => Boolean(
  folder?.spec?.productName && !folder.spec.productFolderId,
);

/** 위 제품의 두께를 저장하는 규격 폴더. */
export const isReportSpecFolder = (folder) => Boolean(folder?.spec?.productFolderId);

/**
 * 아이콘 우측 하단 사각형.
 * 문서명(일반문서·인증서)과 제품명(성적서 제품·가공품)은 호박색 하나다.
 * 규격명만 초록이다. 성적서 가공품 아래에는 규격 폴더를 두지 않는다.
 */
export const folderAttributeMark = (folder) => {
  if (!folderHasAttribute(folder)) return null;
  if (isReportSpecFolder(folder)) return { label: '규격명', color: '#159A55' };
  const color = '#E08A1E';
  if (isNamedProductFolder(folder) || (folder.rootKind === 'TEST' && isProcessSpecFolder(folder))) {
    return { label: '제품명', color };
  }
  return { label: '문서명', color };
};

/** 유리 구성이 있는 가공품. 이 폴더에서 속성이 끝난다. */
export const isProcessSpecFolder = (folder) => Boolean(
  folder?.spec?.glass && typeof folder.spec.glass === 'object',
);

/** 위쪽으로 올라가 처음 만나는 제품 폴더. */
export const ancestorNamedProduct = (folder, folders) => {
  const byId = foldersById(folders);
  let parentId = folder?.parentId;
  const seen = new Set();
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent) return null;
    if (isNamedProductFolder(parent)) return parent;
    parentId = parent.parentId;
  }
  return null;
};

/** TEST 성적서 트리에서의 역할. 그 외 루트는 null. */
export const folderRole = (folder, folders) => {
  if (!folder || folder.rootKind !== 'TEST') return null;
  if (isProcessSpecFolder(folder)) return 'process';
  if (isNamedProductFolder(folder)) return 'product';
  if (isReportSpecFolder(folder) || ancestorNamedProduct(folder, folders)) return 'spec';
  return 'group';
};

/**
 * 묶음·제품 아래에만 폴더를 만든다.
 * 가공품과 규격(두께) 폴더 아래는 막는다.
 */
export const canAddChildFolder = (folder, folders) => {
  if (!folder || folder.linkView) return false;
  const role = folderRole(folder, folders);
  if (!role) return true;
  return role === 'group' || role === 'product';
};

export const canRenameFolder = (folder) => (
  folder && !folder.linkView && !isSystemRootFolder(folder)
);

/** Phase 2 이후 하위 폴더 없음 여부 (참고용). 템플릿은 문서 슬롯 전부 허용 */
export const isLeafFolder = (folder, folders) => {
  if (!folder || isSystemRootFolder(folder)) return false;
  return !folders.some((f) => f.parentId === folder.id);
};

export const buildFolderPath = (folder, folderMap) => {
  if (!folder) return '';
  const names = [];
  let cur = folder;
  while (cur) {
    names.unshift(cur.name);
    cur = cur.parentId ? folderMap[cur.parentId] : null;
  }
  return names.join(' › ');
};

/** Windows 탐색기형 와일드카드: * = 0자 이상, ? = 1자. 패턴에 *? 없으면 부분일치(*query*) */
export const matchWildcard = (text, pattern) => {
  if (!pattern || !pattern.trim()) return true;
  const raw = pattern.trim();
  const hasWild = /[*?]/.test(raw);
  const effective = hasWild ? raw : `*${raw}*`;
  const escaped = effective
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  try {
    return new RegExp(`^${escaped}$`, 'i').test(text || '');
  } catch (_e) {
    return (text || '').toLowerCase().includes(raw.toLowerCase());
  }
};

/**
 * 검색 패턴의 리터럴(* ? 제외)을 텍스트에서 찾아 하이라이트 조각으로 나눈다.
 * @returns {{ text: string, match: boolean }[]}
 */
export const splitWildcardHighlights = (text, pattern) => {
  const str = text == null ? '' : String(text);
  const raw = (pattern || '').trim();
  if (!str || !raw) return [{ text: str, match: false }];
  const literals = raw.split(/[*?]+/).filter(Boolean);
  if (!literals.length) return [{ text: str, match: false }];
  const unique = [...new Set(literals)].sort((a, b) => b.length - a.length);
  let re;
  try {
    const escaped = unique.map((l) => l.replace(/[.+^${}()|[\]\\]/g, '\\$&'));
    re = new RegExp(`(${escaped.join('|')})`, 'gi');
  } catch (_e) {
    return [{ text: str, match: false }];
  }
  const chunks = [];
  let last = 0;
  let m = re.exec(str);
  while (m) {
    if (m.index > last) chunks.push({ text: str.slice(last, m.index), match: false });
    chunks.push({ text: m[0], match: true });
    last = m.index + m[0].length;
    if (m[0].length === 0) break;
    m = re.exec(str);
  }
  if (last < str.length) chunks.push({ text: str.slice(last), match: false });
  return chunks.length ? chunks : [{ text: str, match: false }];
};

/** rootId 이하 자손 폴더 id 집합 (root 자신 포함) */
export const collectDescendantIds = (folders, rootId) => {
  const ids = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    folders.forEach((f) => {
      if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) {
        ids.add(f.id);
        changed = true;
      }
    });
  }
  return ids;
};

/**
 * 현재 폴더 이하에서 폴더명·사용 중 문서명 검색.
 * 현재 폴더 자신은 결과 폴더로 넣지 않지만, 그 안의 사용중 문서는 검색한다.
 * @returns {{ kind: 'folder'|'file', folder, file?, path }[]}
 */
export const searchInSubtree = (folders, folderMap, rootId, query, rootCurrentFile) => {
  const q = (query || '').trim();
  if (!q || !rootId) return [];
  const scope = collectDescendantIds(folders, rootId);
  const results = [];

  folders.forEach((f) => {
    if (!scope.has(f.id)) return;
    const path = buildFolderPath(f, folderMap);
    if (f.id !== rootId && matchWildcard(f.name, q)) {
      results.push({ kind: 'folder', folder: f, path });
    }
    const current = (f.id === rootId && rootCurrentFile) ? rootCurrentFile : f.currentFile;
    if (current?.fileName && matchWildcard(current.fileName, q)) {
      results.push({
        kind: 'file',
        folder: f,
        file: current,
        path,
      });
    }
  });

  results.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
    return (a.path || '').localeCompare(b.path || '', 'ko');
  });
  return results;
};

export const selectTree = ({ ensureDefaults = true } = {}) => async (dispatch) => {
  const qs = ensureDefaults ? '' : '?ensureDefaults=false';
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/tree${qs}`);
  const data = await response.json();
  dispatch(setSubMatFolders(data));
  return data;
};

/** 전체 업로드 직전. 루트와 기본 하위 폴더가 없으면 만든다. */
export const ensureCompanyFolders = () => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/ensureDefaults`, {
    method: 'POST',
  });
  const data = await response.json();
  dispatch(setSubMatFolders(data));
  return data;
};

export const select = (id) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${id}`);
  const data = await response.json();
  dispatch(setSubMatFolder(data.subMatFolder));
  return data.subMatFolder;
};

/** 추가 서류로 붙일 폴더 목록을 바꾼다. */
export const replaceFolderLinks = (folderId, targetFolderIds) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/links`, {
    method: 'PUT',
    body: JSON.stringify({ targetFolderIds }),
  });
  const data = await response.json();
  dispatch(setSubMatFolderLinks(data.subMatFolderLinks || []));
  return data.subMatFolderLinks || [];
};

export const selectChildren = (parentId) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${parentId}/children`);
  const data = await response.json();
  return data.subMatFolders;
};

export const create = (payload) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.subMatFolder) dispatch(addSubMatFolder(data.subMatFolder));
  return data.subMatFolder;
};

export const modify = (payload) => async (dispatch, getState) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  const updated = data.subMatFolder;
  // 탐색기에서 자식 폴더 이름을 바꿀 때 현재 위치를 그 폴더로 바꾸지 않는다
  if (updated && getState().subMatFolder.subMatFolder?.id === updated.id) {
    dispatch(setSubMatFolder(updated));
  }
  return updated;
};

/** 프로젝트 New. 제품 속성이 있는 폴더. */
export const selectProductFolders = async () => {
  const response = await subMatFetch(null, `${gcoreServerUrl}/api/subMatFolders/query/products`);
  const data = await response.json();
  return data.subMatFolders || [];
};

/** 프로젝트 New. 인증규격이 설정된 성적서 말단. */
export const selectTerminals = async ({ g04docuGCertificationId, gclientId, code } = {}) => {
  const qs = new URLSearchParams();
  if (g04docuGCertificationId) qs.set('g04docuGCertificationId', g04docuGCertificationId);
  if (gclientId) qs.set('gclientId', gclientId);
  if (code) qs.set('code', code);
  const response = await subMatFetch(null, `${gcoreServerUrl}/api/subMatFolders/query/terminals?${qs.toString()}`);
  const data = await response.json();
  return data.subMatFolders || [];
};

/** 폴더 속성. spec가 없으면 비운다. replaceFolderId 가 있으면 그 폴더의 속성을 빼고 이 폴더에 넣는다. */
export const saveFolderSpec = (id, spec, replaceFolderId) => async (dispatch, getState) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${id}/spec`, {
    method: 'PUT',
    body: JSON.stringify({
      spec: spec || null,
      ...(replaceFolderId ? { replaceFolderId } : {}),
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '속성 저장 실패');
    e.serverResponse = err;
    throw e;
  }
  const data = await response.json();
  const updated = data.subMatFolder;
  if (!updated) return updated;
  const nextFolder = spec ? updated : { ...updated, spec: null };
  const clearedId = data.clearedFolderId || null;
  const state = getState().subMatFolder;
  dispatch(setSubMatFolders({
    subMatFolders: state.subMatFolders.map((folder) => {
      if (folder.id === nextFolder.id) {
        return { ...folder, ...nextFolder, currentFile: nextFolder.currentFile ?? folder.currentFile };
      }
      if (clearedId && folder.id === clearedId) return { ...folder, spec: null };
      return folder;
    }),
    count: state.count,
  }));
  if (state.subMatFolder?.id === nextFolder.id) {
    dispatch(setSubMatFolder(nextFolder));
  } else if (clearedId && state.subMatFolder?.id === clearedId) {
    dispatch(setSubMatFolder({ ...state.subMatFolder, spec: null }));
  }
  return nextFolder;
};

export const remove = (id) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${id}`, {
    method: 'DELETE',
  });
  return response;
};

/** allowRename: false면 동명 시 throw (errorCode DUPLICATE). true면 이름 (2) */
export const moveFolder = (id, targetParentId, allowRename = false) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${id}/move`, {
    method: 'POST',
    body: JSON.stringify({ targetParentId, allowRename }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '이동 실패');
    e.serverResponse = err;
    e.status = response.status;
    throw e;
  }
  const data = await response.json();
  return data.subMatFolder;
};

export const copyFolder = (id, targetParentId, allowRename = false) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify({ targetParentId, allowRename }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '복사 실패');
    e.serverResponse = err;
    e.status = response.status;
    throw e;
  }
  const data = await response.json();
  return data.subMatFolder;
};

export const isDuplicateNameError = (err) => {
  const sr = err?.serverResponse || err;
  const code = sr?.errorCode ?? sr?.code;
  return code === 12050
    || code === 'DUPLICATE_SUB_MAT_FOLDER_NAME'
    || String(sr?.message || err?.message || '').includes('동일한 이름');
};

export const selectFiles = (folderId) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files`);
  const data = await response.json();
  dispatch(setSubMatFolderFiles({ folderId, files: data.subMatFolderFiles }));
  return data.subMatFolderFiles;
};

/** 묶어 올린 시험성과대비표 PDF를 성적서와 같은 폴더에 짝으로 넣는다. */
export const importTestComparisonFileDirect = async (
  folderId,
  reportFileId,
  file,
  { gstandardId, leadData, rows } = {},
) => {
  const formData = new FormData();
  formData.append('file', file);
  if (gstandardId) formData.append('gstandardId', gstandardId);
  if (leadData) formData.append('leadData', leadData);
  if (rows) formData.append('rows', rows);
  const headers = actingGclientHeaders({ 'XSRF-Token': Cookies.get('XSRF-TOKEN') });
  const accessToken = sessionStorage.getItem('accessToken');
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await window.fetch(
    `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${reportFileId}/testComparisonFile`,
    { method: 'POST', headers, body: formData },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const error = new Error(err.message || '시험성과대비표를 올리지 못했습니다.');
    error.serverResponse = err;
    throw error;
  }
  const data = await response.json();
  return data.subMatFolderFile;
};

export const uploadFile = (folderId, file) => async (dispatch) => {
  const formData = new FormData();
  formData.append('file', file);
  const headers = actingGclientHeaders({ 'XSRF-Token': Cookies.get('XSRF-TOKEN') });
  const accessToken = sessionStorage.getItem('accessToken');
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const response = await window.fetch(`${gcoreServerUrl}/api/subMatFolders/${folderId}/files`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!response.ok) {
    throw response;
  }
  const data = await response.json();
  return data.subMatFolderFile;
};

export const setCurrentFile = (folderId, fileId) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${fileId}/current`, {
    method: 'PUT',
  });
  const data = await response.json();
  return data.subMatFolderFile;
};

/** 파일 이름 바꾸기(디스크+DB). 성적서면 pair 대비표도 {이름}_성과대비표.pdf 연동 */
export const renameFile = (folderId, fileId, fileName) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${fileId}`, {
    method: 'PUT',
    body: JSON.stringify({ fileName }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '이름 바꾸기에 실패했습니다.');
    e.serverResponse = err;
    e.status = response.status;
    throw e;
  }
  const data = await response.json();
  return data.subMatFolderFile;
};

/** 서류 만료일 저장 */
export const updateFileExpiryDirect = async (folderId, fileId, { endDate, validYN } = {}) => {
  const response = await subMatFetch(null, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${fileId}/expiry`, {
    method: 'PUT',
    body: JSON.stringify({ endDate: endDate || null, validYN: Boolean(validYN) }),
  });
  const data = await response.json();
  return data.subMatFolderFile;
};

/** 국세/지방세 만료일 자동판독 (저장 없음). taxType: NATIONAL_TAX | LOCAL_TAX */
export const extractFileExpiryDirect = async (folderId, fileId, taxType) => {
  const response = await subMatFetch(null, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${fileId}/extractExpiry`, {
    method: 'POST',
    body: JSON.stringify({ taxType }),
  });
  const data = await response.json();
  return data.endDate || null;
};

/** 폴더형 대체성적서 후보. 성적서 폴더의 현재 파일. */
export const selectSubstituteCandidatesDirect = async ({ gclientId } = {}) => {
  const qs = new URLSearchParams();
  if (gclientId) qs.set('gclientId', gclientId);
  const q = qs.toString();
  const response = await subMatFetch(
    null,
    `${gcoreServerUrl}/api/subMatFolders/files/substituteCandidates${q ? `?${q}` : ''}`,
  );
  const data = await response.json();
  return data.subMatFolderFiles || [];
};

/** 만료일 일괄점검 목록 */
export const selectExpiryReviewDirect = async ({ gclientId, expiryStatus, fileName, rootKind } = {}) => {
  const qs = new URLSearchParams();
  if (gclientId) qs.set('gclientId', gclientId);
  if (expiryStatus) qs.set('expiryStatus', expiryStatus);
  if (fileName) qs.set('fileName', fileName);
  if (rootKind) qs.set('rootKind', rootKind);
  const q = qs.toString();
  const response = await subMatFetch(
    null,
    `${gcoreServerUrl}/api/subMatFolders/files/expiryReview${q ? `?${q}` : ''}`,
  );
  const data = await response.json();
  return data.subMatFolderFiles || [];
};

export const removeFile = (folderId, fileId) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/files/${fileId}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  return data.subMatFolderFile;
};

/** 성적서 폴더 시험성과대비표 추출 (reportFileId + jsonData/leadData) */
export const extractTestComparison = (folderId, {
  gstandardId,
  reportFileId,
  jsonData,
} = {}) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/testComparison/extract`, {
    method: 'POST',
    body: JSON.stringify({
      gstandardId: gstandardId || null,
      reportFileId: reportFileId || null,
      jsonData: jsonData || null,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '성적서 분석에 실패했습니다.');
    e.serverResponse = err;
    throw e;
  }
  const data = await response.json();
  return data.testResult;
};

/** 성적서에 묶인 시험성과대비표 행·메타 조회 (재편집) */
export const selectTestComparisonDetail = (folderId, reportFileId) => async (dispatch) => {
  const qs = reportFileId ? `?reportFileId=${encodeURIComponent(reportFileId)}` : '';
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/testComparison${qs}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '시험성과대비표 조회에 실패했습니다.');
    e.serverResponse = err;
    throw e;
  }
  return response.json();
};

/** 시험성과대비표 PDF + 행 데이터 저장 (성적서 pair, 사용중 유지) */
export const createTestComparisonPdf = (folderId, {
  g04docuGTestComparisons,
  productName,
  reportFileId,
  gstandardId,
  jsonData,
}) => async (dispatch) => {
  const response = await subMatFetch(dispatch, `${gcoreServerUrl}/api/subMatFolders/${folderId}/testComparison/pdf`, {
    method: 'POST',
    body: JSON.stringify({
      g04docuGTestComparisons,
      productName,
      reportFileId: reportFileId || null,
      gstandardId: gstandardId || null,
      jsonData: jsonData || null,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.message || '시험성과대비표 PDF 저장에 실패했습니다.');
    e.serverResponse = err;
    throw e;
  }
  const data = await response.json();
  return data.subMatFolderFile;
};

export const LINK_NODE_PREFIX = 'link:';

/** 이 폴더가 붙어 있는 대상 폴더. */
export const linkTargetsOf = (sourceId, folders, links) => {
  const byId = foldersById(folders);
  return (links || [])
    .filter((link) => link.sourceFolderId === sourceId)
    .map((link) => byId.get(link.targetFolderId))
    .filter(Boolean);
};

/** target에서 따라가면 source에 닿으면 서로 물린다. */
export const linkWouldCycle = (links, sourceId, targetId) => {
  if (!sourceId || !targetId || sourceId === targetId) return true;
  const outgoing = new Map();
  (links || []).forEach((link) => {
    if (!link || link.sourceFolderId === sourceId) return;
    if (!outgoing.has(link.sourceFolderId)) outgoing.set(link.sourceFolderId, []);
    outgoing.get(link.sourceFolderId).push(link.targetFolderId);
  });
  const seen = new Set();
  const stack = [targetId];
  while (stack.length) {
    const current = stack.pop();
    if (!current || seen.has(current)) continue;
    seen.add(current);
    if (current === sourceId) return true;
    (outgoing.get(current) || []).forEach((next) => stack.push(next));
  }
  return false;
};

/** 붙여 둔 폴더 안에 보이는 줄. 실물 자식으로 이미 있으면 줄을 또 만들지 않는다. */
export const linkViewsForTarget = (targetId, folders, links) => {
  if (!targetId || !links?.length) return [];
  const byId = foldersById(folders);
  const target = byId.get(targetId);
  return (links || [])
    .filter((link) => link.targetFolderId === targetId)
    .map((link) => {
      const source = byId.get(link.sourceFolderId);
      if (!source || source.parentId === targetId) return null;
      return {
        id: `${LINK_NODE_PREFIX}${link.id}`,
        linkView: true,
        linkId: link.id,
        sourceFolderId: source.id,
        name: source.name,
        rootKind: source.rootKind,
        spec: source.spec,
        currentFile: source.currentFile,
        parentId: targetId,
        isSystem: false,
        folderLevel: (target?.folderLevel || 0) + 1,
        updatedAt: source.updatedAt,
        createdAt: source.createdAt,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ko'));
};

export const linkViewById = (nodeId, folders, links) => {
  if (!nodeId || !String(nodeId).startsWith(LINK_NODE_PREFIX)) return null;
  const linkId = String(nodeId).slice(LINK_NODE_PREFIX.length);
  const link = (links || []).find((item) => item.id === linkId);
  if (!link) return null;
  return linkViewsForTarget(link.targetFolderId, folders, [link])[0] || null;
};

export const childrenWithLinks = (realFolders, allFolders, links, parentId) => {
  const real = buildFolderTree(realFolders, parentId);
  const realSourceIds = new Set(real.map((folder) => folder.id));
  const views = linkViewsForTarget(parentId, allFolders, links)
    .filter((view) => !realSourceIds.has(view.sourceFolderId));
  return [...real, ...views];
};

export const buildFolderTree = (folders, parentId = null) => {
  return folders
    .filter((f) => (f.parentId || null) === parentId)
    .sort((a, b) => {
      // 최상위(CERT/GENERAL/TEST/ETC 등)는 시스템 order 유지
      if (parentId == null) {
        const byOrder = (a.order || 0) - (b.order || 0);
        if (byOrder !== 0) return byOrder;
        return (a.name || '').localeCompare(b.name || '', 'ko');
      }
      // 최상위 아래는 가나다 정렬
      return (a.name || '').localeCompare(b.name || '', 'ko');
    });
};

const initialState = {
  subMatFolders: [],
  subMatFolderLinks: [],
  count: 0,
  subMatFolder: null,
  subMatFolderFiles: [],
  filesFolderId: null,
};

function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_SUB_MAT_FOLDERS:
      return {
        ...state,
        subMatFolders: action.payload.subMatFolders,
        subMatFolderLinks: action.payload.subMatFolderLinks || [],
        count: action.payload.count,
      };
    case SET_SUB_MAT_FOLDER_LINKS:
      return {
        ...state,
        subMatFolderLinks: action.payload || [],
      };
    case SET_SUB_MAT_FOLDER: {
      const updated = action.payload;
      return {
        ...state,
        subMatFolder: updated,
        subMatFolders: state.subMatFolders.map((f) => (
          f.id === updated.id
            ? {
              ...f,
              ...updated,
              currentFile: updated.currentFile ?? f.currentFile,
            }
            : f
        )),
      };
    }
    case SET_SUB_MAT_FOLDER_FILES:
      return {
        ...state,
        subMatFolderFiles: action.payload.files,
        filesFolderId: action.payload.folderId,
      };
    case ADD_SUB_MAT_FOLDER: {
      const created = action.payload;
      if (!created?.id) return state;
      const exists = state.subMatFolders.some((f) => f.id === created.id);
      const subMatFolders = exists
        ? state.subMatFolders.map((f) => (f.id === created.id ? { ...f, ...created } : f))
        : [...state.subMatFolders, created];
      return {
        ...state,
        subMatFolders,
        count: subMatFolders.length,
      };
    }
    default:
      return state;
  }
}

export default reducer;
